import type { ResolutionEnumNoPrefix } from '@talos/kyoko';
import {
  EMPTY_ARRAY,
  isMultileg,
  MixpanelEventSource,
  MixpanelSourceContext,
  useSecuritiesContext,
} from '@talos/kyoko';
import type { AgAnnotation, AgPriceVolumeChartType } from 'ag-charts-enterprise';
import { useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { getDeepDiveRoute } from '../../../../../containers/Routes/routes';
import { DeepDive } from '../../../../../containers/Trading/DeepDive/DeepDive';
import { useCurrentAppLayoutId } from '../../../../../hooks/useAppLayoutId';
import { useAppLayoutConfig } from '../../../../../providers/AppLayoutConfig/AppLayoutConfigContext';
import { DeepDiveProvider } from '../../../../../providers/DeepDiveProvider';
import type { DeepDivePanelConfig } from '../../../../../types/LayoutConfig';
import { Panel } from '../../../../Layout/Components/Panel';
import { getNavGroupSlug } from '../../../../NavigationSidebar/spacesNavPath';
import { DEFAULT_LAYOUT_ID } from '../../../Layouts/tokens';
import { TEMPORARY_ID } from '../../../tokens';
import type { LayoutPanelProps } from '../../../types';
import { useBuildDeepDiveSymbolChangePanelParams } from './useBuildDeepDiveSymbolChangePanelParams';

export function DeepDivePanel({ params, api }: LayoutPanelProps<DeepDivePanelConfig>) {
  const navigate = useNavigate();
  const {
    customTitle,
    symbol,
    markets,
    marketAccounts,
    showMarkets,
    chartResolution,
    priceIncrement,
    showCumQty,
    aggregation,
    annotations,
    chartType,
  } = params;
  const { securitiesBySymbol, searchableSecurities } = useSecuritiesContext();
  const layoutId = useCurrentAppLayoutId() ?? DEFAULT_LAYOUT_ID;
  const { layoutConfigs } = useAppLayoutConfig('trading');
  const navGroupName = useMemo(() => getNavGroupSlug(layoutConfigs, layoutId), [layoutConfigs, layoutId]);

  const deepDiveSecurities = useMemo(() => searchableSecurities.filter(s => !isMultileg(s)), [searchableSecurities]);
  const effectiveSymbol = symbol ?? deepDiveSecurities[0]?.Symbol;

  const buildSymbolChangePanelParams = useBuildDeepDiveSymbolChangePanelParams();

  const setMarketAccounts = useCallback(
    (marketAccounts: string[]) => {
      api.updateParameters({ marketAccounts });
    },
    [api]
  );

  const setMarkets = useCallback(
    (markets: string[]) => {
      api.updateParameters({ markets });
    },
    [api]
  );

  const setAggregation = useCallback(
    (aggregation: string | undefined) => {
      api.updateParameters({ aggregation });
    },
    [api]
  );

  const setSymbol = useCallback(
    (nextSymbol: string) => {
      const nextPanelParams = buildSymbolChangePanelParams(nextSymbol);
      navigate(
        getDeepDiveRoute({
          symbol: nextSymbol,
          id: api.id === TEMPORARY_ID ? undefined : api.id,
          layoutId,
          navGroupName,
          aggregation: nextPanelParams.aggregation,
        }),
        {
          replace: true,
        }
      );
      api.updateParameters(nextPanelParams);
      if (!customTitle) {
        api.setTitle(securitiesBySymbol.get(nextSymbol)?.DisplaySymbol ?? nextSymbol);
      }
    },
    [api, navigate, customTitle, securitiesBySymbol, layoutId, navGroupName, buildSymbolChangePanelParams]
  );

  const setShowMarkets = useCallback(
    (showMarkets: boolean) => {
      api.updateParameters({ showMarkets });
    },
    [api]
  );

  const setChartResolution = useCallback(
    (chartResolution: ResolutionEnumNoPrefix) => {
      api.updateParameters({ chartResolution });
    },
    [api]
  );

  const setShowCumQty = useCallback(
    (showCumQty: boolean) => {
      api.updateParameters({ showCumQty });
    },
    [api]
  );

  const setPriceIncrement = useCallback(
    (priceIncrement: string) => {
      api.updateParameters({ priceIncrement });
    },
    [api]
  );

  const setAnnotations = useCallback(
    (annotations: AgAnnotation[] | undefined) => {
      api.updateParameters({ annotations });
    },
    [api]
  );

  const setChartType = useCallback(
    (chartType: AgPriceVolumeChartType) => {
      api.updateParameters({ chartType });
    },
    [api]
  );

  const handleRemovePanel = useCallback(() => {
    api.close();
  }, [api]);

  const selectedMarkets = markets ?? EMPTY_ARRAY;
  const selectedMarketAccounts = marketAccounts ?? EMPTY_ARRAY;

  return (
    <Panel panelApi={api} data-testid="deep-dive-panel">
      <MixpanelSourceContext.Provider value={MixpanelEventSource.DeepDive}>
        <DeepDiveProvider
          symbol={effectiveSymbol}
          setSymbol={setSymbol}
          selectedMarkets={selectedMarkets}
          selectedMarketAccounts={selectedMarketAccounts}
          selectedAggregation={aggregation}
          setSelectedMarketAccounts={setMarketAccounts}
          setSelectedMarkets={setMarkets}
          setSelectedAggregation={setAggregation}
          setShowMarkets={setShowMarkets}
          showMarkets={!!showMarkets}
          closeDeepDive={handleRemovePanel}
          chartResolution={chartResolution}
          setChartResolution={setChartResolution}
          setShowCumQty={setShowCumQty}
          setPriceIncrement={setPriceIncrement}
          showCumQty={showCumQty}
          priceIncrement={priceIncrement}
          annotations={annotations}
          chartType={chartType}
          setAnnotations={setAnnotations}
          setChartType={setChartType}
        >
          <DeepDive resizing={false} />
        </DeepDiveProvider>
      </MixpanelSourceContext.Provider>
    </Panel>
  );
}
